import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ShieldCheckIcon } from '@heroicons/react/20/solid';

import WidgetBase from './WidgetBase';
import { classNames } from '../helpers/functions';

import useAsync from '../../hooks/useAsync';
import useAxios from '../../hooks/useAxios';
import { getIntegrationByWidgetKey } from '../../api/company';

const WIDGET_KEY = 'larian-tech-asset-monitoring';

export const LarianTechAssetWidget = ({ assetId }) => {
	const navigate = useNavigate();

	const { value: widgetData } = useAsync(getIntegrationByWidgetKey, {
		defaultPayload: { id: WIDGET_KEY },
	});

	const DEVICE_MAPPING = widgetData?.configuration?.deviceMapping;
	const API_KEY = widgetData?.apiKey;
	const DEVICE_ID = DEVICE_MAPPING ? DEVICE_MAPPING[assetId] : null;

	const {
		value: monitoringData,
		execute: onCallEndpoint,
		status: loadingData,
	} = useAxios();

	const handleRefreshData = () => {
		onCallEndpoint(
			`https://external.lariantech.cloud/device/v2/getPaginatedDeviceData`,
			{
				method: 'POST',
				data: {
					from: moment().utc().startOf('D').format('YYYY-MM-DD HH:mm:SS'),
					to: moment().utc().format('YYYY-MM-DD HH:mm:00'),
					deviceIds: [DEVICE_ID],
					limit: 1,
				},
				headers: {
					'x-api-key': API_KEY,
				},
			}
		);
	};

	useEffect(() => {
		if (API_KEY && DEVICE_ID) {
			handleRefreshData();
		}
	}, [API_KEY, DEVICE_ID]);

	const deviceMonitoringData = monitoringData?.data?.[DEVICE_ID]?.length
		? monitoringData?.data[DEVICE_ID][0]
		: {};

	const stats = [
		...(deviceMonitoringData?.temperature
			? [
					{
						name: 'Temperature',
						value: `${
							deviceMonitoringData?.temperature
								? `${deviceMonitoringData?.temperature}°`
								: 'N/A'
						}`,
						change: '',
						changeType: 'positive',
					},
			  ]
			: []),
		...(deviceMonitoringData?.temperature
			? [
					{
						name: 'Humidity',
						value: `${
							deviceMonitoringData?.humidity
								? `${deviceMonitoringData?.humidity}%`
								: 'N/A'
						}`,
						change: '',
						changeType: 'positive',
					},
			  ]
			: []),
		...(deviceMonitoringData?.co2
			? [
					{
						name: 'CO2',
						value: `${
							deviceMonitoringData?.co2
								? `${deviceMonitoringData?.co2}%`
								: 'N/A'
						}`,
						change: '',
						changeType: 'positive',
					},
			  ]
			: []),
		...(deviceMonitoringData?.thermocouple
			? [
					{
						name: 'Thermocouple',
						value: `${
							deviceMonitoringData?.thermocouple
								? `${deviceMonitoringData?.thermocouple}°`
								: 'N/A'
						}`,
						change: '',
						changeType: 'positive',
					},
			  ]
			: []),
	];

	// TODO: Add reports to list item

	return (
		<WidgetBase
			title="Monitoring"
			subtitle={
				<div className="flex items-center">
					Powered by LarianTech{' '}
					<ShieldCheckIcon
						className="ml-3 w-5 h-5 text-green-500 cursor-pointer"
						onClick={() => setOpen({ id: item?.id })}
					/>
				</div>
			}
			menuItems={[
				{
					name: 'Refresh Data',
					onClick: () => handleRefreshData(),
				},
				{
					name: 'View Integration',
					onClick: () =>
						navigate(`/settings/integrations/${widgetData?.integrationId}`),
				},
			]}
			cols={2}
			isLoading={loadingData === 'pending'}
			isError={loadingData === 'error'}
			isEmpty={Object.keys(stats)?.length === 0}
			isInactive={!API_KEY || !DEVICE_ID}
			onRefresh={() => handleRefreshData()}
			onIntegrationClick={() =>
				navigate(`/settings/integrations/${widgetData?.integration?.id}`)
			}
			flexCenter={true}
		>
			<dl className="mx-auto grid grid-cols-1 gap-px sm:grid-cols-2">
				{stats.map((stat) => (
					<div
						key={stat.name}
						className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 bg-white px-4 py-6 sm:px-6"
					>
						<dt className="text-sm font-medium leading-6 text-gray-500">
							{stat.name}
						</dt>
						<dd
							className={classNames(
								stat.changeType === 'negative'
									? 'text-rose-600'
									: 'text-gray-700',
								'text-xs font-medium'
							)}
						>
							{stat.change}
						</dd>
						<dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900">
							{stat.value}
						</dd>
					</div>
				))}
			</dl>
			<div className="mt-4 text-gray-400 flex justify-between w-full">
				<div>{`Last Fetch: ${moment(deviceMonitoringData?.time)
					.utc()
					.format('MMMM	Do, YYYY [at] h:mmA')}`}</div>
				<div>{`Device Id: ${DEVICE_ID}`}</div>
			</div>
		</WidgetBase>
	);
};
